import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useCart } from "@/hooks/useCart";
import { useSellerNotifications } from "@/hooks/useSellerNotifications";
import { useNotifications } from "@/hooks/useNotifications";
import { useChristmasTheme } from "@/hooks/useChristmasTheme";
import BrandLogo from "./BrandLogo";
import SmartSearch from "./SmartSearch";

interface SidebarProps {
  onNavigate?: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const MAIN_ITEMS: NavItem[] = [
  { to: "/", label: "Inicio", icon: "ri-home-5-line" },
  { to: "/#categorias", label: "Categorías", icon: "ri-apps-2-line" },
  { to: "/#destacados", label: "Destacados", icon: "ri-fire-line" },
];

export default function Sidebar({ onNavigate }: SidebarProps) {
  const { user, profile, role, accountType, sellerStatus, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { totalItems } = useCart();
  const { pendingCount: pendingSellers } = useSellerNotifications();
  const { unreadCount: unreadNotifications } = useNotifications();
  const {
    enabled: christmasEnabled,
    snow,
    animations,
    setEnabled,
    setSnow,
    setAnimations,
  } = useChristmasTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // El botón "Publicar" solo está disponible para vendedores aprobados,
  // compradores sin solicitud y administradores. Se oculta mientras la
  // solicitud de vendedor está en revisión o la cuenta está suspendida.
  const showPublish =
    sellerStatus !== "pending" && sellerStatus !== "suspended";

  const isActive = (to: string): boolean => {
    if (to === "/") return location.pathname === "/" && !location.hash;
    if (to.startsWith("/#")) {
      return location.pathname === "/" && location.hash === to.slice(1);
    }
    return location.pathname === to;
  };

  const handleSignOut = async () => {
    await signOut();
    onNavigate?.();
    navigate("/login", { replace: true });
  };

  const panelPath =
    role === "super_admin"
      ? "/super-admin"
      : role === "admin"
        ? "/admin/dashboard"
        : accountType === "seller"
          ? "/seller/dashboard"
          : null;

  // "Publicar" siempre abre el formulario de publicación (las mismas opciones
  // que ve un vendedor). Admin y Super Admin pueden publicar directamente.
  const canPublish =
    sellerStatus === "approved" ||
    accountType === "seller" ||
    role === "admin" ||
    role === "super_admin";
  const publishPath = canPublish ? "/seller/dashboard" : "/registro";

  const linkClasses = (active: boolean) =>
    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
      active
        ? "bg-primary-50 text-primary-700"
        : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
    }`;

  return (
    <div className="flex h-full flex-col border-r border-background-200/70 bg-background-50">
      {/* Marca */}
      <div className="flex h-16 shrink-0 items-center px-5">
        <BrandLogo size="lg" showDot />
      </div>

      {/* Buscador inteligente */}
      <div className="shrink-0 px-4 pb-3">
        <SmartSearch variant="compact" onNavigate={onNavigate} />
      </div>

      {/* Navegación */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        <p className="px-3 pb-1.5 pt-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
          Menú
        </p>
        <ul className="flex flex-col gap-0.5">
          {MAIN_ITEMS.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                onClick={(event) => {
                  onNavigate?.();
                  // Si ya estamos en el inicio y el enlace apunta a una
                  // sección, forzamos el salto directo (react-router no
                  // cambia la ubicación cuando el ancla es la misma).
                  if (item.to.startsWith("/#") && location.pathname === "/") {
                    const target = document.querySelector(item.to.slice(1));
                    if (target) {
                      event.preventDefault();
                      target.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }
                }}
                className={linkClasses(isActive(item.to))}
              >
                <i className={`${item.icon} text-lg`}></i>
                {item.label}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/carrito"
              onClick={onNavigate}
              className={linkClasses(isActive("/carrito"))}
            >
              <span className="relative flex h-6 w-6 items-center justify-center">
                <i className="ri-shopping-cart-2-line text-lg"></i>
                {totalItems > 0 && (
                  <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold leading-none text-white">
                    {totalItems > 99 ? "99+" : totalItems}
                  </span>
                )}
              </span>
              Carrito
            </Link>
          </li>
          {showPublish && (
            <li>
              <Link
                to={publishPath}
                onClick={onNavigate}
                className={linkClasses(isActive(publishPath))}
              >
                <i className="ri-add-circle-line text-lg"></i>
                Publicar
              </Link>
            </li>
          )}
        </ul>

        <p className="px-3 pb-1.5 pt-5 text-xs font-semibold uppercase tracking-wide text-foreground-400">
          Cuenta
        </p>
        <ul className="flex flex-col gap-0.5">
          {user ? (
            <>
              <li>
                <Link
                  to="/notificaciones"
                  onClick={onNavigate}
                  className={linkClasses(isActive("/notificaciones"))}
                >
                  <span className="relative flex h-6 w-6 items-center justify-center">
                    <i className="ri-notification-3-line text-lg"></i>
                    {unreadNotifications > 0 && (
                      <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold leading-none text-white">
                        {unreadNotifications > 99 ? "99+" : unreadNotifications}
                      </span>
                    )}
                  </span>
                  Notificaciones
                </Link>
              </li>
              <li>
                <Link
                  to="/mensajes"
                  onClick={onNavigate}
                  className={linkClasses(isActive("/mensajes"))}
                >
                  <i className="ri-chat-3-line text-lg"></i>
                  Mensajes
                </Link>
              </li>
              <li>
                <Link
                  to="/perfil"
                  onClick={onNavigate}
                  className={linkClasses(isActive("/perfil"))}
                >
                  <i className="ri-user-3-line text-lg"></i>
                  Mi perfil
                </Link>
              </li>
              {panelPath && (
                <li>
                  <Link
                    to={panelPath}
                    onClick={onNavigate}
                    className={linkClasses(isActive(panelPath))}
                  >
                    <i className="ri-dashboard-3-line text-lg"></i>
                    {role === "super_admin" || role === "admin"
                      ? "Panel de administración"
                      : "Mi Tienda"}
                  </Link>
                </li>
              )}
              {(role === "admin" || role === "super_admin") && (
                <li>
                  <Link
                    to="/admin/vendedores"
                    onClick={onNavigate}
                    className={linkClasses(isActive("/admin/vendedores"))}
                  >
                    <span className="relative flex h-6 w-6 items-center justify-center">
                      <i className="ri-store-2-line text-lg"></i>
                      {pendingSellers > 0 && (
                        <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold leading-none text-white">
                          {pendingSellers > 99 ? "99+" : pendingSellers}
                        </span>
                      )}
                    </span>
                    Solicitudes de vendedores
                  </Link>
                </li>
              )}
              <li>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-950"
                >
                  <i className="ri-logout-box-r-line text-lg"></i>
                  Cerrar sesión
                </button>
              </li>
            </>
          ) : (
            <>
              <li>
                <Link
                  to="/login"
                  onClick={onNavigate}
                  className={linkClasses(isActive("/login"))}
                >
                  <i className="ri-login-box-line text-lg"></i>
                  Iniciar sesión
                </Link>
              </li>
              <li>
                <Link
                  to="/registro"
                  onClick={onNavigate}
                  className={linkClasses(isActive("/registro"))}
                >
                  <i className="ri-user-add-line text-lg"></i>
                  Crear cuenta
                </Link>
              </li>
            </>
          )}
        </ul>
      </nav>

      {/* Pie: usuario + tema */}
      <div className="shrink-0 border-t border-background-200/70 p-3">
        {user && (
          <div className="mb-2 flex items-center gap-3 rounded-md bg-background-100 px-3 py-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
              {(profile?.full_name ?? profile?.email ?? "U").charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground-950">
                {profile?.full_name ?? "Usuario"}
              </p>
              <p className="truncate text-xs text-foreground-500">
                {role === "super_admin"
                  ? "Super Admin"
                  : role === "admin"
                    ? "Administrador"
                    : accountType === "seller"
                      ? "Vendedor"
                      : "Comprador"}
              </p>
            </div>
          </div>
        )}

        {/* Preferencias de Navidad (campaña visual temporal) */}
        <div className="mb-2 flex flex-col gap-0.5 rounded-md bg-background-100 p-1.5">
          <button
            type="button"
            onClick={() => setEnabled(!christmasEnabled)}
            className="flex w-full cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
          >
            <span className="flex items-center gap-2">
              <span aria-hidden="true">🎄</span> Navidad
            </span>
            <i
              className={`text-lg ${
                christmasEnabled
                  ? "ri-toggle-fill text-secondary-500"
                  : "ri-toggle-line text-foreground-400"
              }`}
            ></i>
          </button>
          {christmasEnabled && (
            <>
              <button
                type="button"
                onClick={() => setSnow(!snow)}
                className="flex w-full cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">❄️</span> Nieve
                </span>
                <i
                  className={`text-lg ${
                    snow
                      ? "ri-toggle-fill text-secondary-500"
                      : "ri-toggle-line text-foreground-400"
                  }`}
                ></i>
              </button>
              <button
                type="button"
                onClick={() => setAnimations(!animations)}
                className="flex w-full cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">✨</span> Animaciones
                </span>
                <i
                  className={`text-lg ${
                    animations
                      ? "ri-toggle-fill text-secondary-500"
                      : "ri-toggle-line text-foreground-400"
                  }`}
                ></i>
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={toggleTheme}
          className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-950"
        >
          <i className={`text-lg ${theme === "dark" ? "ri-sun-line" : "ri-moon-line"}`}></i>
          {theme === "dark" ? "Modo claro" : "Modo oscuro"}
        </button>
      </div>
    </div>
  );
}